import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/api";
import BaseModal from "./BaseModal";

// ─── Helpers ────────────────────────────────────────────────────────────────

const fmt = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);

const Spinner = ({ label }) => (
  <div className="flex flex-col items-center gap-4 py-16">
    <svg
      className="animate-spin h-10 w-10 text-blue-500"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 22.627 0 12 0v4z"
      />
    </svg>
    <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
  </div>
);

// ─── Main Component ──────────────────────────────────────────────────────────

const PositionsImportModal = ({ file, accounts = [], onClose, onSuccess }) => {
  const navigate = useNavigate();

  // step: "previewing" | "picking" | "importing" | "error"
  const [step, setStep] = useState("previewing");
  const [errorMsg, setErrorMsg] = useState(null);

  // Preview data — list of { csv_account_number, csv_account_name, position_count, cash_balance, total_value }
  const [csvAccounts, setCsvAccounts] = useState([]);

  // Per-CSV-account UI state: { [csv_account_number]: { included, targetAccountId, createNew, newAccountName } }
  const [mappings, setMappings] = useState({});

  // Capture accounts at mount — prevents runPreview from re-firing when the
  // parent refreshes the account list after a successful import.
  const accountsRef = useRef(accounts);

  // ── Step 1: preview on mount ───────────────────────────────────────────────

  const runPreview = useCallback(async () => {
    setStep("previewing");
    setErrorMsg(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/positions/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fetched = res.data.accounts || [];

      if (fetched.length === 0) {
        setErrorMsg("No accounts found in this file.");
        setStep("error");
        return;
      }

      setCsvAccounts(fetched);

      // Build initial mappings — auto-match by name using the ref so this
      // callback doesn't need `accounts` as a dep (avoids re-fire on refresh).
      const currentAccounts = accountsRef.current;
      const initialMappings = {};
      fetched.forEach((csvAcct) => {
        const nameMatch = currentAccounts.find(
          (a) =>
            a.name.toLowerCase() === csvAcct.csv_account_name.toLowerCase(),
        );
        initialMappings[csvAcct.csv_account_number] = {
          included: true,
          targetAccountId: nameMatch ? nameMatch.id : "__create__",
          createNew: !nameMatch,
          newAccountName: csvAcct.csv_account_name,
        };
      });
      setMappings(initialMappings);
      setStep("picking");
    } catch (err) {
      setErrorMsg(
        err.response?.data?.detail ||
          "Failed to read the file. Please try again.",
      );
      setStep("error");
    }
  }, [file]); // `accounts` removed — use accountsRef so effect doesn't re-fire on parent refresh

  useEffect(() => {
    runPreview();
  }, [runPreview]);

  // ── Mapping helpers ────────────────────────────────────────────────────────

  const updateMapping = (csvAcctNum, patch) => {
    setMappings((prev) => ({
      ...prev,
      [csvAcctNum]: { ...prev[csvAcctNum], ...patch },
    }));
  };

  const handleSelectChange = (csvAcctNum, value, csvAcctName) => {
    if (value === "__create__") {
      updateMapping(csvAcctNum, {
        targetAccountId: "__create__",
        createNew: true,
        newAccountName: csvAcctName,
      });
    } else {
      updateMapping(csvAcctNum, {
        targetAccountId: value,
        createNew: false,
        newAccountName: null,
      });
    }
  };

  const checkedCount = Object.values(mappings).filter((m) => m.included).length;

  // ── Step 3: import — create accounts + kick off background tasks ───────────

  const handleImport = async () => {
    setStep("importing");
    setErrorMsg(null);
    try {
      const mappingPayload = Object.entries(mappings)
        .filter(([, m]) => m.included)
        .map(([csvAcctNum, m]) => ({
          csv_account_number: csvAcctNum,
          target_account_id: m.createNew ? null : m.targetAccountId,
          create_new: m.createNew,
          new_account_name: m.createNew ? m.newAccountName || null : null,
        }));

      const fd = new FormData();
      fd.append("file", file);
      fd.append("account_mappings", JSON.stringify(mappingPayload));

      const res = await api.post("/positions/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const { imported_accounts } = res.data;

      // Accounts are created; background import is running.
      // Close modal, trigger parent refresh, and navigate to the new account.
      // The NewDashboardLayout polling loop will show the sync spinner and
      // do a final refresh when the import completes — exactly like SnapTrade.
      onClose();
      if (onSuccess) onSuccess();
      if (imported_accounts?.length > 0) {
        navigate(`/account/${imported_accounts[0].account_id}`);
      }
    } catch (err) {
      setErrorMsg(
        err.response?.data?.detail || "Import failed. Please try again.",
      );
      setStep("error");
    }
  };

  // ── Render helpers ─────────────────────────────────────────────────────────

  const renderHeader = () => {
    const titles = {
      previewing: "Import Positions",
      picking: "Import Accounts",
      importing: "Importing…",
      error: "Import Failed",
    };
    const subtitles = {
      picking: `${csvAccounts.length} account${csvAccounts.length !== 1 ? "s" : ""} found in file`,
    };
    return (
      <div className="px-6 py-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {titles[step] ?? "Import Positions"}
          </h2>
          {subtitles[step] && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {subtitles[step]}
            </p>
          )}
        </div>
        {step !== "importing" && step !== "previewing" && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-1 rounded"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>
    );
  };

  const renderBody = () => {
    if (step === "previewing") return <Spinner label="Analyzing file…" />;
    if (step === "importing") return <Spinner label="Creating accounts…" />;

    if (step === "error") {
      return (
        <div className="px-6 py-10 flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <svg
              className="w-6 h-6 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <p className="text-sm text-red-600 dark:text-red-400 text-center max-w-sm">
            {errorMsg}
          </p>
          <button
            onClick={runPreview}
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            Try again
          </button>
        </div>
      );
    }

    if (step === "picking") {
      return (
        <div className="px-6 py-4 space-y-3">
          {csvAccounts.map((csvAcct) => {
            const m = mappings[csvAcct.csv_account_number] || {};
            const isCreateNew =
              m.createNew || m.targetAccountId === "__create__";

            return (
              <div
                key={csvAcct.csv_account_number}
                className={`border rounded-lg p-4 transition-colors ${
                  m.included
                    ? "border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-900/10"
                    : "border-gray-200 dark:border-gray-700 opacity-60"
                }`}
              >
                {/* Top row: checkbox + name + stats */}
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={m.included ?? true}
                    onChange={(e) =>
                      updateMapping(csvAcct.csv_account_number, {
                        included: e.target.checked,
                      })
                    }
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 cursor-pointer"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white text-sm">
                          {csvAcct.csv_account_name}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                          #{csvAcct.csv_account_number}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {fmt(csvAcct.total_value)}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          {csvAcct.position_count} position
                          {csvAcct.position_count !== 1 ? "s" : ""} ·{" "}
                          {fmt(csvAcct.cash_balance)} cash
                        </p>
                      </div>
                    </div>

                    {/* Account selector */}
                    <div className="mt-3 space-y-2">
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                        Import into
                      </label>
                      <select
                        disabled={!m.included}
                        value={m.targetAccountId ?? "__create__"}
                        onChange={(e) =>
                          handleSelectChange(
                            csvAcct.csv_account_number,
                            e.target.value,
                            csvAcct.csv_account_name,
                          )
                        }
                        className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value="__create__">
                          — Create new account —
                        </option>
                        {accounts.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.name}
                          </option>
                        ))}
                      </select>

                      {/* New account name input */}
                      {isCreateNew && m.included && (
                        <input
                          type="text"
                          placeholder="New account name"
                          value={m.newAccountName || ""}
                          onChange={(e) =>
                            updateMapping(csvAcct.csv_account_number, {
                              newAccountName: e.target.value,
                            })
                          }
                          className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    return null;
  };

  const renderFooter = () => {
    if (step === "previewing" || step === "importing" || step === "error")
      return null;

    if (step === "picking") {
      return (
        <div className="px-6 py-4 flex items-center justify-between gap-3">
          <button
            onClick={onClose}
            className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={checkedCount === 0}
            className="px-5 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Import{" "}
            {checkedCount > 0
              ? `${checkedCount} account${checkedCount !== 1 ? "s" : ""}`
              : ""}
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <BaseModal
      header={renderHeader()}
      footer={renderFooter()}
      onClose={step === "importing" || step === "previewing" ? null : onClose}
      closeOnBackdrop={step !== "importing" && step !== "previewing"}
      maxWidth="max-w-2xl"
    >
      {renderBody()}
    </BaseModal>
  );
};

export default PositionsImportModal;
